import _axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18n from 'i18next';
import {
  clearPortalTokens,
  getPortalTenantId,
  getPortalTokens,
  setPortalTokens,
} from './portalUtils';

const baseURL = process.env.REACT_APP_API_ADDRESS;
const timeout = 8000; // 8 sec

// A dedicated instance rather than the staff one: it carries portal tokens,
// resolves its tenant from the portal slug in the URL instead of the stored
// staff workspace, and must never fall back to a staff session.
const portalAxios = _axios.create({ baseURL, timeout });

// Set by PortalAuthProvider once the slug in the URL is known. Kept in module
// scope so the interceptors can reach it without threading it through every
// call site.
let activeSlug = '';

export const setActivePortalSlug = (slug: string) => {
  activeSlug = slug;
};

export const getActivePortalSlug = () => activeSlug;

// Called on an unrecoverable auth failure so the provider can drop back to
// the login screen; assigned by PortalAuthProvider.
let onAuthFailure: (() => void) | null = null;

export const setPortalAuthFailureHandler = (handler: (() => void) | null) => {
  onAuthFailure = handler;
};

portalAxios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!config.headers['Accept-Language']) {
    config.headers['Accept-Language'] = i18n.language;
  }

  const tenantId = getPortalTenantId(activeSlug);
  if (!config.headers.Tenant && tenantId) {
    config.headers.Tenant = tenantId;
  }

  const { access } = getPortalTokens(activeSlug);
  if (access && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${access}`;
  }

  return config;
});

portalAxios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    const isRefreshCall = originalRequest?.url?.includes('/auth/token/refresh/');

    if (error.response?.status !== 401 || !originalRequest || isRefreshCall) {
      return Promise.reject(error);
    }

    const slug = activeSlug;
    const { refresh } = getPortalTokens(slug);
    if (!refresh) {
      clearPortalTokens(slug);
      onAuthFailure?.();
      return Promise.reject(error);
    }

    try {
      // Refreshing goes out on a bare instance: the interceptor above would
      // otherwise attach the expired access token that caused this 401.
      const refreshAxios = _axios.create({
        baseURL,
        timeout,
        headers: { Tenant: getPortalTenantId(slug) },
      });
      const response = await refreshAxios.post('/portal/auth/token/refresh/', { refresh });

      setPortalTokens(slug, { access: response.data.access });
      originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
      return portalAxios(originalRequest);
    } catch (refreshError) {
      clearPortalTokens(slug);
      onAuthFailure?.();
      return Promise.reject(refreshError);
    }
  }
);

export default portalAxios;
